import { supabase } from "@/integrations/supabase/client";
import { TokenService } from "@/services/auth/TokenService";
import { ImpersonationService } from "@/services/users/ImpersonationService";

export interface ApiRequestOptions {
  resource: string;
  action: string;
  id?: string | number;
  data?: Record<string, any>;
  headers?: Record<string, string>;
}

export interface ApiResponse<T = any> {
  data?: T;
  success?: boolean;
  message?: string;
  error?: any;
}

/**
 * Public-lane calls attach a token when one exists but never wait for one:
 * an anonymous visitor has no session to wait for, and the retry backoff
 * would otherwise sit in front of every public page load.
 */
const resolveToken = (mode: string): Promise<string | null> =>
  mode === "auth" ? TokenService.getAccessToken() : TokenService.getAccessTokenIfSignedIn();

export class ApiService {
  static async invoke<T = any>(options: ApiRequestOptions, mode: string = "auth"): Promise<T> {
    // if (options.resource === "profiles" && options.action === "get") {
    //   console.trace(`[TRACE] profiles.get id=${options.id}`);
    // }
    const token = await resolveToken(mode);

    if (!token && mode === "auth") throw new Error("Not authenticated");
    const impersonatedUserId = ImpersonationService.getImpersonatedUserId();

    const { headers: customHeaders, ...requestBody } = options;

    const body = impersonatedUserId
      ? {
          ...requestBody,
          data: {
            ...(requestBody.data || {}),
            // When impersonating, scope API reads/writes to target user.
            adminMode: true,
            userId: requestBody.data?.userId ?? impersonatedUserId,
            impersonatedUserId: requestBody.data?.impersonatedUserId ?? impersonatedUserId,
          },
        }
      : requestBody;

    const headers = {
      ...(token ? { Authorization: `Bearer ${token}` } : { "x-public-request": "true" }),
      ...(customHeaders || {}),
    };

    // Branch previews use their matching API for every resource/action.
    // Preserve the older conversation-only override for existing deployments.
    const functionName = import.meta.env.VITE_API_FUNCTION || (options.resource === "conversations" && options.action === "get"
      ? import.meta.env.VITE_CONVERSATION_API_FUNCTION || "api"
      : "api");
    const { data: response, error } = await supabase.functions.invoke(functionName, {
      headers,
      body,
    });

    if (error || (response && !response.success)) {
      console.log("API invocation error:", error, response);
      if (response?.error?.message) {
        const apiError = new Error(response.error.message) as Error & { apiError: any };
        apiError.apiError = response.error;
        throw apiError;
      }
      throw error || new Error("API invocation failed");
    }

    return response?.data as T;
  }

  static async callEdge<T = any>(functionName: string, body: any, mode: string = "auth"): Promise<T> {
    const token = await resolveToken(mode);

    if (!token && mode === "auth") throw new Error("Not authenticated");

    const { data, error } = await supabase.functions.invoke(functionName, {
      headers: token ? { Authorization: `Bearer ${token}` } : { "x-public-request": "true" },
      body,
    });

    if (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const userFriendlyError = errorMessage.includes("Edge Function returned a non-2xx status code")
        ? new Error("Oops, some error occurred")
        : error;
      throw userFriendlyError;
    }

    return data as T;
  }
}
